import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaBookOpen, FaRegBuilding } from "react-icons/fa";
import { HiDotsVertical } from "react-icons/hi";
import { IoChevronBack, IoChevronForward, IoClose } from "react-icons/io5";
import { FiSend } from "react-icons/fi";
import { Button } from "@mui/material";
import moment from "moment";
import styles from "../ClassWiseApproval/ViewClasswiseApproval.module.css";
import ScheduleTable from "../../../../../components/Scheduletable/Scheduletable";
import PopupModal from "../../../../../components/PopupModal/PopupModal";

import AxiosInstance, { TokenService } from "../../../../../services/Api";

// ------------------------------------------------------------------
// Map numeric lesson_plan_status -> keyword
// 1 Submitted, 2 Approved, 3 Rejected, 4 Resubmitted, 5 Completed
// ------------------------------------------------------------------
const LESSON_PLAN_STATUS_MAP = {
  1: "submitted",
  2: "approved",
  3: "rejected",
  4: "resubmitted",
  5: "completed",
};

// Reverse: keyword -> numeric code (used when sending status to APIs
// that expect the integer code rather than the keyword string).
const LESSON_PLAN_STATUS_CODE = {
  submitted: 1,
  approved: 2,
  rejected: 3,
  resubmitted: 4,
  completed: 5,
};

const DAY_SHORT = {
  Sunday: "Sun",
  Monday: "Mon",
  Tuesday: "Tue",
  Wednesday: "Wed",
  Thursday: "Thu",
  Friday: "Fri",
  Saturday: "Sat",
};

const SELECTABLE_STATUSES = new Set(["submitted", "resubmitted"]);

/* ==================================================================
   Send Reminder Modal
   ================================================================== */
const SendReminderModal = ({
  open,
  onClose,
  onConfirm,
  loading,
  weekRangeLabel,
  periods,
  schedule,
  translations,
}) => {
  const t = translations?.labels || {};

  if (!open) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !loading) onClose?.();
  };

  const getPeriodData = (dayPeriods, periodId) => {
    if (!Array.isArray(dayPeriods)) return null;
    return (
      dayPeriods.find((p) => String(p.periodId) === String(periodId)) || null
    );
  };

  return (
    <div
      className={styles.modalBackdrop}
      onMouseDown={handleBackdropClick}
      role="presentation"
    >
      <div
        className={`${styles.modal} ${styles.modalLg}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="send-reminder-title"
      >
        <div className={styles.modalHeader}>
          <h3 id="send-reminder-title" className={styles.modalTitle}>
            {t.sendReminder || "Send Reminder"}
          </h3>
          <button
            type="button"
            className={styles.modalCloseBtn}
            onClick={onClose}
            disabled={loading}
            aria-label={t.close || "Close"}
          >
            <IoClose />
          </button>
        </div>

        <div className={styles.modalBody}>
          <p className={styles.reminderIntro}>
            {t.reminderIntroLine1 ||
              `The highlighted periods in the timetable below indicate pending lesson plan submissions for the selected schedule${
                weekRangeLabel ? ` (${weekRangeLabel})` : ""
              }.`}
            <br />
            {t.reminderIntroLine2 ||
              "Would you like to send a reminder notification now?"}
          </p>

          <div className={styles.reminderTableWrap}>
            <table className={styles.reminderTable}>
              <thead>
                <tr className={styles.reminderHeadRow}>
                  <th
                    className={styles.reminderHeadCell}
                    style={{ width: "110px" }}
                  />
                  {periods.map((p) => (
                    <th key={p.id} className={styles.reminderHeadCell}>
                      <span className={styles.reminderHeadPeriodLabel}>
                        {p.label}
                      </span>
                      {p.time ? (
                        <span className={styles.reminderHeadPeriodTime}>
                          {p.time}
                        </span>
                      ) : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {schedule.map((day) => (
                  <tr key={day.rawDate || day.day}>
                    <td className={styles.reminderDayCell}>
                      <span className={styles.reminderDayName}>{day.day}</span>
                      {day.date ? (
                        <span className={styles.reminderDayDate}>
                          ({day.date})
                        </span>
                      ) : null}
                    </td>
                    {periods.map((p) => {
                      const cell = getPeriodData(day.periods, p.id);
                      const hasContent = cell && cell.subject;
                      const isPending =
                        hasContent &&
                        (cell._rawStatus === null ||
                          cell._rawStatus === undefined);
                      return (
                        <td
                          key={`${day.day}-${p.id}`}
                          className={`${styles.reminderBodyCell} ${
                            isPending ? styles.reminderPendingCell : ""
                          }`}
                        >
                          {hasContent ? (
                            <>
                              <span className={styles.reminderSubjectText}>
                                {cell.subject}
                              </span>
                              {cell.section ? (
                                <span className={styles.reminderSectionText}>
                                  {cell.section}
                                </span>
                              ) : null}
                            </>
                          ) : (
                            <span className={styles.reminderEmptyText}>
                              ---
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button
            type="button"
            className={`${styles.modalBtn} ${styles.modalCancelBtn}`}
            onClick={onClose}
            disabled={loading}
          >
            {t.cancel || "Cancel"}
          </button>
          <button
            type="button"
            className={`${styles.modalBtn} ${styles.modalConfirmBtn}`}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading
              ? t.sending || "Sending..."
              : t.yesSendReminder || "Yes, Send Reminder"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ==================================================================
   Rejection Reason Modal
   ================================================================== */
const RejectionReasonModal = ({
  open,
  onClose,
  onConfirm,
  loading,
  translations,
}) => {
  const t = translations?.labels || {};
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!open) {
      setReason("");
      setError("");
      setFocused(false);
    }
  }, [open]);

  if (!open) return null;

  const handleConfirm = () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setError(t.reasonRequired || "Please provide a reason for rejection");
      return;
    }
    onConfirm?.(trimmed);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !loading) onClose?.();
  };

  const inputClass = [
    styles.rejectInput,
    focused && !error ? styles.rejectInputFocused : "",
    error ? styles.rejectInputError : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={styles.modalBackdrop} onMouseDown={handleBackdropClick}>
      <div
        className={`${styles.modal} ${styles.modalSm} ${styles.modalAccentTop}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="plan-rejection-title"
      >
        <div className={styles.modalHeader}>
          <h3 id="plan-rejection-title" className={styles.modalTitle}>
            {t.reasonForRejection || "Reason for Rejection"}
          </h3>
          <button
            type="button"
            className={styles.modalCloseBtn}
            onClick={onClose}
            disabled={loading}
            aria-label={t.close || "Close"}
          >
            <IoClose />
          </button>
        </div>

        <div className={styles.modalBody}>
          <label
            className={styles.rejectInputLabel}
            htmlFor="plan-rejection-input"
          >
            {t.reason || "Reason"}
          </label>
          <input
            id="plan-rejection-input"
            type="text"
            className={inputClass}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (error) setError("");
            }}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            disabled={loading}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") handleConfirm();
              if (e.key === "Escape") onClose?.();
            }}
          />
          {error && <div className={styles.rejectErrorText}>{error}</div>}
        </div>

        <div className={styles.modalFooter}>
          <button
            type="button"
            className={`${styles.modalBtn} ${styles.modalCancelBtn}`}
            onClick={onClose}
            disabled={loading}
          >
            {t.cancel || "Cancel"}
          </button>
          <button
            type="button"
            className={`${styles.modalBtn} ${styles.modalConfirmBtn}`}
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading
              ? t.submitting || "Submitting..."
              : t.yesReject || "Yes, Reject"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ViewTeacherWiseApproval = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { translations } = useSelector((state) => state.language);
  const t = translations?.labels || translations || {};
  const [modalState, setModalState] = useState({
    open: false,
    type: "",
    message: "",
    redirectPath: "",
  });
  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const updated_by = Number(user?.id) || 0;

  // ------------------------------------------------------------------
  // Route state — accept staff_id/teacher_id at top level or nested
  // ------------------------------------------------------------------
  const routeState = location.state || {};
  const routeData = routeState.data || {};

  const rawStaffId =
    routeState.staff_id ??
    routeState.teacher_id ??
    routeData.staff_id ??
    routeData.teacher_id ??
    null;

  const staff_id =
    rawStaffId != null && rawStaffId !== "" ? Number(rawStaffId) : null;

  const routeStaffName =
    routeState.staff_name ??
    routeState.teacher_name ??
    routeData.staff_name ??
    routeData.teacher_name ??
    "";

  const initialDate = routeState.schedule_date ?? moment().format("YYYY-MM-DD");

  // If staff_id is missing, DO NOT call APIs — they will 400 otherwise.
  const hasRequiredIds = useMemo(() => {
    return (
      staff_id != null &&
      !Number.isNaN(staff_id) &&
      school_id !== "" &&
      school_id != null
    );
  }, [staff_id, school_id]);

  // ------------------------------------------------------------------
  // State
  // ------------------------------------------------------------------
  const [scheduleDate, setScheduleDate] = useState(initialDate);

  const [loading, setLoading] = useState(false);
  const [weekStart, setWeekStart] = useState("");
  const [weekEnd, setWeekEnd] = useState("");
  const [timetable, setTimetable] = useState([]);

  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansDate, setPlansDate] = useState(initialDate);
  // Status of the cell that was clicked to load this plan list. Sent as
  // numeric code in /book/staffwise-lesson-plan-view payload so the
  // backend can filter to plans matching that status.
  const [selectedCellStatus, setSelectedCellStatus] = useState("");

  // hm_id comes from the lesson-plan-view response. It's required in the
  // two status-update APIs. We keep a top-level fallback for bulk updates
  // that span multiple plans.
  const [fallbackHmId, setFallbackHmId] = useState(null);

  const [selectedCells, setSelectedCells] = useState([]);

  const [sendingReminder, setSendingReminder] = useState(false);
  const [reminderModalOpen, setReminderModalOpen] = useState(false);

  const [planRejectModal, setPlanRejectModal] = useState({
    open: false,
    plan: null,
  });
  const [planActionSubmitting, setPlanActionSubmitting] = useState(false);

  const [approvingAll, setApprovingAll] = useState(false);

  // ------------------------------------------------------------------
  // POST /book/staffwise-timetable
  // ------------------------------------------------------------------
  const fetchStaffwiseTimetable = useCallback(async () => {
    if (!hasRequiredIds) {
      setTimetable([]);
      setWeekStart("");
      setWeekEnd("");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        school_id,
        staff_id,
        schedule_date: scheduleDate,
      };

      const response = await AxiosInstance.post(
        `/book/staffwise-timetable`,
        payload,
      );

      if (response?.data?.status && response?.data?.data) {
        const {
          week_start = "",
          week_end = "",
          timetable: days = [],
        } = response.data.data;

        setWeekStart(week_start);
        setWeekEnd(week_end);
        setTimetable(Array.isArray(days) ? days : []);

        // Capture hm_id from timetable response if the API includes it.
        const topHmId = response.data.data.hm_id ?? response.data.hm_id ?? null;
        if (topHmId != null) setFallbackHmId(Number(topHmId));

        if (week_start && week_start !== scheduleDate) {
          setScheduleDate(week_start);
        }
      } else {
        setTimetable([]);
        console.error("Staffwise timetable failed:", response?.data?.message);
      }
    } catch (error) {
      console.error("Staffwise timetable fetch error:", error);
      setTimetable([]);
    } finally {
      setLoading(false);
    }
  }, [hasRequiredIds, school_id, staff_id, scheduleDate]);

  useEffect(() => {
    fetchStaffwiseTimetable();
  }, [fetchStaffwiseTimetable]);

  // ------------------------------------------------------------------
  // POST /book/staffwise-lesson-plan-view
  // Captures hm_id per plan (and a top-level fallback) for later
  // use in the bulk-update and update-status APIs.
  // ------------------------------------------------------------------
  const fetchLessonPlans = useCallback(
    async (date, status = "") => {
      if (!date) return;
      if (!hasRequiredIds) {
        setPlans([]);
        return;
      }

      setPlansLoading(true);
      try {
        // Convert the keyword status (e.g. "approved") into the numeric
        // code (e.g. 2) the backend expects. Empty string when nothing
        // is selected — backend treats it as "all".
        const statusCode = status
          ? LESSON_PLAN_STATUS_CODE[status] ?? ""
          : "";
        const payload = {
          school_id,
          staff_id,
          date,
          status: statusCode,
        };

        const response = await AxiosInstance.post(
          `/book/staffwise-lesson-plan-view`,
          payload,
        );

        if (response?.data?.status && response?.data?.data) {
          const data = response.data.data;
          const dayGroups = data.lesson_plan || [];

          // Top-level hm_id fallback (accept several common shapes).
          const topHmId = data.hm_id ?? response.data.hm_id ?? null;
          if (topHmId != null) setFallbackHmId(Number(topHmId));

          const flat = [];
          dayGroups.forEach((dayGroup) => {
            const groupHmId =
              dayGroup.hm_id != null ? Number(dayGroup.hm_id) : null;

            (dayGroup.lesson_plans || []).forEach((lp, idx) => {
              // Prefer per-plan hm_id, then day-group hm_id, then top-level.
              const planHmId =
                lp.hm_id != null
                  ? Number(lp.hm_id)
                  : groupHmId != null
                    ? groupHmId
                    : topHmId != null
                      ? Number(topHmId)
                      : null;

              flat.push({
                key: `${dayGroup.date}-${lp.def_id ?? "d"}-${
                  lp.item_id ?? idx
                }`,
                date: dayGroup.date,
                day_name: dayGroup.day_name,
                def_id: lp.def_id,
                item_id: lp.item_id,
                book_id: lp.book_id,
                book_name: lp.book_name || "",
                topic: lp.topic || "",
                sub_topic: lp.sub_topic || "",
                medium: lp.medium || "",
                status: lp.status,
                statusKey: LESSON_PLAN_STATUS_MAP[lp.status] || "submitted",
                content: lp.content || "",
                start_page: lp.start_page,
                end_page: lp.end_page,
                staff_comment: lp.staff_comment || "",
                hm_id: planHmId,
              });
            });
          });

          setPlans(flat);
        } else {
          setPlans([]);
          console.error(
            "Staffwise lesson plan view failed:",
            response?.data?.message,
          );
        }
      } catch (error) {
        console.error("Lesson plan view fetch error:", error);
        setPlans([]);
      } finally {
        setPlansLoading(false);
      }
    },
    [hasRequiredIds, school_id, staff_id],
  );

  useEffect(() => {
    fetchLessonPlans(plansDate, selectedCellStatus);
  }, [fetchLessonPlans, plansDate, selectedCellStatus]);

  // ------------------------------------------------------------------
  // Week navigation
  // ------------------------------------------------------------------
  const handlePrevWeek = useCallback(() => {
    setScheduleDate((prev) => {
      const newDate = moment(prev).subtract(7, "days");
      const today = moment().startOf("day");

      // Block if the entire previous week is before today
      // (check if the END of that week is still before today)
      if (newDate.clone().add(6, "days").isBefore(today)) {
        return prev;
      }

      return newDate.format("YYYY-MM-DD");
    });
  }, []);

  const handleNextWeek = useCallback(() => {
    setScheduleDate((prev) => moment(prev).add(7, "days").format("YYYY-MM-DD"));
  }, []);

  // ------------------------------------------------------------------
  // Send Reminder modal
  // ------------------------------------------------------------------
  const handleOpenReminderModal = useCallback(() => {
    if (sendingReminder) return;
    setReminderModalOpen(true);
  }, [sendingReminder]);

  const handleCloseReminderModal = useCallback(() => {
    if (sendingReminder) return;
    setReminderModalOpen(false);
  }, [sendingReminder]);

  const handleConfirmSendReminder = useCallback(async () => {
    if (sendingReminder) return;
    if (!hasRequiredIds) return;

    const payload = {
      school_id,
      staff_id,
      schedule_date: scheduleDate,
      created_by: updated_by,
    };

    try {
      setSendingReminder(true);
      const response = await AxiosInstance.post(
        `/book/staffwise/send-reminder`,
        payload,
      );
      if (response?.data?.status) {
        setReminderModalOpen(false);
        setModalState({
          open: true,
          type: "success",
          message:
            t?.SentReminderSuccessfully || "sent reminders successfully.",
          redirectPath: "",
        });
      } else {
        console.error("Send reminder failed:", response?.data?.message);
        setModalState({
          open: true,
          type: "error",
          message: t?.SentReminderFailed || response?.data?.message,
        });
      }
    } catch (error) {
      console.error("Send reminder error:", error);
      setModalState({
        open: true,
        type: "error",
        message: t?.SentReminderFailed || "Send reminder failed",
      });
    } finally {
      setSendingReminder(false);
    }
  }, [
    sendingReminder,
    hasRequiredIds,
    school_id,
    staff_id,
    scheduleDate,
    updated_by,
    t,
  ]);

  // ------------------------------------------------------------------
  // Derive period columns
  // ------------------------------------------------------------------
  const periods = useMemo(() => {
    const map = new Map();
    timetable.forEach((day) => {
      (day.periods || []).forEach((p) => {
        const key = String(p.period);
        if (!map.has(key)) {
          const start = p.break_start_time || "";
          const end = p.break_end_time || "";
          map.set(key, {
            id: key,
            label: p.period_name || `Period ${p.period}`,
            time: start && end ? `${start} - ${end}` : start || end || "",
          });
        }
      });
    });
    return Array.from(map.values()).sort((a, b) => Number(a.id) - Number(b.id));
  }, [timetable]);

  // ------------------------------------------------------------------
  // Transform timetable -> ScheduleTable rows
  // Staff view: subtitle shows "Class-Section".
  // ------------------------------------------------------------------
  const schedule = useMemo(() => {
    return timetable.map((day) => {
      const dayName = day.day_name || "";
      const displayDate = day.date ? moment(day.date).format("DD/MM") : "";

      const periodsRow = (day.periods || []).map((p) => ({
        periodId: String(p.period),
        subject: p.subject_name || "",
        section:
          p.class_name && p.section_id
            ? `${p.class_name}-${p.section_id}`
            : p.class_name || "",
        status:
          p.lesson_plan_status != null
            ? LESSON_PLAN_STATUS_MAP[p.lesson_plan_status] || ""
            : "",
        _rowId: p.id,
        _rawStatus: p.lesson_plan_status,
      }));

      return {
        day: DAY_SHORT[dayName] || dayName,
        date: displayDate,
        rawDate: day.date,
        periods: periodsRow,
        isHoliday: !!day.is_holiday,
        holidayReason: day.holiday_reason || "",
      };
    });
  }, [timetable]);

  const legendItems = useMemo(
    () => [
      { key: "submitted", label: t.submitted || "Submitted" },
      { key: "approved", label: t.approved || "Approved" },
      { key: "rejected", label: t.rejected || "Rejected" },
      { key: "resubmitted", label: t.resubmitted || "Resubmitted" },
      { key: "completed", label: t.completed || "Completed" },
    ],
    [t],
  );

  const handleCellClick = useCallback((cellInfo) => {
    if (cellInfo.date) {
      setPlansDate(cellInfo.date);
    }
    // Track the clicked cell's status so the lesson-plan-view API can
    // filter to that status. Submitted/resubmitted cells continue to
    // drive the multi-select selection list below; for the other
    // statuses we still update the filter so the cards refresh.
    setSelectedCellStatus(cellInfo.status || "");

    if (!SELECTABLE_STATUSES.has(cellInfo.status)) {
      return;
    }
    setSelectedCells((prev) => {
      const exists = prev.some(
        (c) =>
          c.date === cellInfo.date &&
          String(c.periodId) === String(cellInfo.periodId),
      );
      if (exists) {
        return prev.filter(
          (c) =>
            !(
              c.date === cellInfo.date &&
              String(c.periodId) === String(cellInfo.periodId)
            ),
        );
      }
      return [
        ...prev,
        {
          date: cellInfo.date,
          periodId: cellInfo.periodId,
          subject: cellInfo.subject,
          section: cellInfo.section,
          status: cellInfo.status,
          rowId: cellInfo.rawPeriod?._rowId,
        },
      ];
    });
  }, []);

  const handleBack = useCallback(() => navigate(-1), [navigate]);

  // ------------------------------------------------------------------
  // Bulk approve — now includes hm_id
  // ------------------------------------------------------------------
  // def_ids + hm_ids that will be sent in the bulk-approve payload.
  // Both arrays are kept in the SAME order so index i in def_ids maps
  // to index i in hm_ids (the API expects paired arrays).
  //
  // The `plans` list is already filtered by the API based on the cell
  // the user clicked (date + status). So the bulk-approve payload
  // should target every approvable plan in that list.
  //
  // De-dup is by composite key (def_id + item_id), NOT by def_id alone
  // — multiple lesson plans can share the same def_id but have
  // different item_id values, and we need to send all of them.
  const { selectedDefIds, selectedHmIds } = useMemo(() => {
    if (!plans.length) return { selectedDefIds: [], selectedHmIds: [] };

    const candidatePlans = plans.filter(
      (p) => SELECTABLE_STATUSES.has(p.statusKey) && p.def_id != null,
    );

    const seenKeys = new Set();
    const defIds = [];
    const hmIds = [];
    candidatePlans.forEach((p) => {
      // Each lesson plan row has a unique hm_id, even when def_id and
      // item_id are duplicated across rows. Dedupe by hm_id so every
      // distinct plan is included in the bulk-update payload.
      const hmId = p.hm_id ?? fallbackHmId ?? null;
      const compositeKey =
        hmId != null
          ? `hm-${hmId}`
          : `${p.def_id}-${p.item_id ?? ""}-${p.date ?? ""}`;
      if (seenKeys.has(compositeKey)) return;
      seenKeys.add(compositeKey);
      defIds.push(p.def_id);
      hmIds.push(hmId);
    });

    return { selectedDefIds: defIds, selectedHmIds: hmIds };
  }, [plans, fallbackHmId]);

  const handleApproveSelected = useCallback(async () => {
    if (approvingAll) return;
    if (!selectedDefIds.length) return;

    const payload = {
      def_ids: selectedDefIds,
      status: 2,
      updated_by,
      comments: "",
      hm_ids: selectedHmIds,
    };

    try {
      setApprovingAll(true);
      const response = await AxiosInstance.put(
        `/book/lesson-plan/bulk-update`,
        payload,
      );

      if (response?.data?.status) {
        setSelectedCells([]);
        // After approval, clear the status filter so the user sees ALL
        // plans for this date (including the just-approved ones), not
        // only the previously-filtered submitted/resubmitted set.
        setSelectedCellStatus("");
        fetchStaffwiseTimetable();
        fetchLessonPlans(plansDate, "");
        setModalState({
          open: true,
          type: "success",
          message:
            t?.SentReminderSuccessfullys ||
            "lesson plans updated successfully.",
          redirectPath: "",
        });
      } else {
        console.error("Bulk approve failed:", response?.data?.message);
        setModalState({
          open: true,
          type: "error",
          message: t?.SentReminderFailed || response?.data?.message,
        });
      }
    } catch (error) {
      console.error("Bulk approve error:", error);
    } finally {
      setApprovingAll(false);
    }
  }, [
    approvingAll,
    selectedDefIds,
    selectedHmIds,
    updated_by,
    fetchStaffwiseTimetable,
    fetchLessonPlans,
    plansDate,
    selectedCellStatus,
  ]);

  // ------------------------------------------------------------------
  // Status -> header strip class
  // ------------------------------------------------------------------
  const statusToClass = useMemo(
    () => ({
      submitted: styles.statusSubmitted,
      approved: styles.statusApproved,
      rejected: styles.statusRejected,
      resubmitted: styles.statusResubmitted,
      completed: styles.statusCompleted,
    }),
    [],
  );

  // ------------------------------------------------------------------
  // 3-dot menu
  // ------------------------------------------------------------------
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuWrapperRef = useRef(null);

  const handleMenuToggle = (e, planKey) => {
    e.stopPropagation();
    setOpenMenuId((prev) => (prev === planKey ? null : planKey));
  };

  useEffect(() => {
    if (openMenuId === null) return;
    const handleOutsideClick = (e) => {
      if (
        menuWrapperRef.current &&
        !menuWrapperRef.current.contains(e.target)
      ) {
        setOpenMenuId(null);
      }
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") setOpenMenuId(null);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [openMenuId]);

  // ------------------------------------------------------------------
  // PUT /book/update-lesson-plan-status — now includes hm_id
  // ------------------------------------------------------------------
  const callUpdatePlanStatus = useCallback(
    async (plan, status, comments = "") => {
      const def_id = plan?.def_id;
      if (def_id == null) return false;

      const hmIdToSend = plan?.hm_id ?? fallbackHmId ?? null;

      const payload = {
        def_id,
        status,
        comments,
        updated_by,
        hm_id: hmIdToSend,
      };

      try {
        setPlanActionSubmitting(true);
        const response = await AxiosInstance.put(
          `/book/update-lesson-plan-status`,
          payload,
        );
        if (response?.data?.status) {
          fetchStaffwiseTimetable();
          fetchLessonPlans(plansDate, selectedCellStatus);
          return true;
        }
        console.error("Update plan status failed:", response?.data?.message);
        return false;
      } catch (error) {
        console.error("Update plan status error:", error);
        return false;
      } finally {
        setPlanActionSubmitting(false);
      }
    },
    [
      updated_by,
      fallbackHmId,
      fetchStaffwiseTimetable,
      fetchLessonPlans,
      plansDate,
      selectedCellStatus,
    ],
  );

  const handleApprovePlan = useCallback(
    (plan) => {
      setOpenMenuId(null);
      if (plan?.statusKey === "approved" || plan?.statusKey === "rejected") {
        return;
      }
      callUpdatePlanStatus(plan, 2, "");
    },
    [callUpdatePlanStatus],
  );

  const handleRejectPlan = useCallback((plan) => {
    setOpenMenuId(null);
    if (plan?.statusKey === "approved" || plan?.statusKey === "rejected") {
      return;
    }
    setPlanRejectModal({ open: true, plan });
  }, []);

  const handleConfirmRejectPlan = useCallback(
    async (reason) => {
      const plan = planRejectModal.plan;
      if (!plan) return;
      const ok = await callUpdatePlanStatus(plan, 3, reason);
      if (ok) setPlanRejectModal({ open: false, plan: null });
    },
    [planRejectModal.plan, callUpdatePlanStatus],
  );

  const handleCancelRejectPlan = useCallback(() => {
    if (planActionSubmitting) return;
    setPlanRejectModal({ open: false, plan: null });
  }, [planActionSubmitting]);

  // ------------------------------------------------------------------
  // Title + week range
  // ------------------------------------------------------------------
  const teacherName = useMemo(() => {
    if (routeStaffName) return routeStaffName;
    const firstDay = timetable.find(
      (d) => Array.isArray(d.periods) && d.periods.length > 0,
    );
    const firstPeriod = firstDay?.periods?.[0];
    return firstPeriod?.staff_name || firstPeriod?.teacher_name || "";
  }, [routeStaffName, timetable]);

  const weekRangeLabel = useMemo(() => {
    if (!weekStart || !weekEnd) return "";
    const start = moment(weekStart);
    const end = moment(weekEnd).add(1, "day");

    if (start.isSame(end, "month")) {
      return `${start.format("MMMM D")} - ${end.format("D, YYYY")}`;
    }
    if (start.isSame(end, "year")) {
      return `${start.format("MMM D")} - ${end.format("MMM D, YYYY")}`;
    }
    return `${start.format("MMM D, YYYY")} - ${end.format("MMM D, YYYY")}`;
  }, [weekStart, weekEnd]);

  // ------------------------------------------------------------------
  // Approve All availability:
  //   The button is enabled ONLY when every visible plan is in a status
  //   that can still be approved — currently "submitted" or "resubmitted".
  //   If any plan is approved, rejected, completed, …, the button stays
  //   disabled.
  // ------------------------------------------------------------------
  // ------------------------------------------------------------------
  // Approve All availability:
  //   STRICT rule: the button is enabled ONLY when EVERY plan in the
  //   currently visible list is in submitted / resubmitted status.
  //   The list is already filtered by the API based on the cell the
  //   user clicked (date + status), so we just check what's on screen.
  //
  //   If any visible plan is approved / rejected / completed, the
  //   button is DISABLED — preventing bulk-approve over finalised
  //   plans.
  // ------------------------------------------------------------------
  const canApproveAll = useMemo(() => {
    if (!plans || plans.length === 0) return false;
    return plans.every((p) => SELECTABLE_STATUSES.has(p.statusKey));
  }, [plans]);

  const handleModalClose = () =>
    setModalState({ open: false, type: "", message: "" });
  const handleSuccessOk = () => {
    const { redirectPath } = modalState;
    setModalState({ open: false, type: "", message: "", redirectPath: "" });

    if (redirectPath && typeof redirectPath === "object" && redirectPath.path) {
      navigate(redirectPath.path, {
        state: redirectPath.state,
        replace: redirectPath.replace || false,
      });
    } else if (typeof redirectPath === "string" && redirectPath.trim() !== "") {
      navigate(redirectPath);
    }
  };
  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  return (
    <>
      <PopupModal
        open={modalState.open}
        type={modalState.type}
        title={
          modalState.type === "success"
            ? "Success"
            : modalState.type || "Failed"
        }
        message={modalState.message}
        onOk={handleSuccessOk}
        onClose={handleModalClose}
      />
      <div className={styles.pageWrapper}>
        <div className={styles.card}>
          <div className={styles.pageHeader}>
            <div className={styles.pageHeaderLeft}>
              <div className={styles.headerIcon}>
                <FaRegBuilding />
              </div>
              <h2 className={styles.headerTitle}>
                {teacherName
                  ? `${teacherName} - ${t.timeTable || "Time Table"}`
                  : t.teacherwiseApproval || "Teacherwise Approval"}
              </h2>
            </div>

            <div className={styles.pageHeaderRight}>
              <button
                type="button"
                className={styles.sendReminderBtn}
                onClick={handleOpenReminderModal}
                disabled={
                  sendingReminder || !weekStart || !weekEnd || !hasRequiredIds
                }
              >
                <FiSend className={styles.sendReminderIcon} />
                <span>
                  {sendingReminder
                    ? t.sending || "Sending..."
                    : t.sendReminder || "Send Reminder"}
                </span>
              </button>

              <div className={styles.weekNav}>
                <button
                  type="button"
                  className={styles.weekNavBtn}
                  onClick={handlePrevWeek}
                  aria-label={t.previousWeek || "Previous week"}
                  disabled={!hasRequiredIds}
                >
                  <IoChevronBack />
                </button>
                <span className={styles.weekNavLabel}>
                  {weekRangeLabel || "-"}
                </span>
                <button
                  type="button"
                  className={styles.weekNavBtn}
                  onClick={handleNextWeek}
                  aria-label={t.nextWeek || "Next week"}
                  disabled={!hasRequiredIds}
                >
                  <IoChevronForward />
                </button>
              </div>
            </div>
          </div>

          {!hasRequiredIds ? (
            <div className={styles.emptyState}>
              {t.missingStaffId ||
                "Missing teacher information. Please go back and open a row from the list again."}
            </div>
          ) : loading ? (
            <div className={styles.emptyState}>{t.loading || "Loading..."}</div>
          ) : (
            <ScheduleTable
              periods={periods}
              schedule={schedule}
              showLegend
              legendItems={legendItems}
              emptyPlaceholder="---"
              selectable
              selectedCells={selectedCells}
              onCellClick={handleCellClick}
            />
          )}
        </div>

        {/* ============ Lesson Plans cards ============ */}
        <div className={styles.plansList}>
          {!hasRequiredIds ? null : plansLoading ? (
            <div className={styles.plansMessage}>
              {t.loading || "Loading..."}
            </div>
          ) : plans.length === 0 ? (
            <div className={styles.plansEmpty}>
              {t.noLessonPlans ||
                `No lesson plans for ${moment(plansDate).format("DD MMM YYYY")}`}
            </div>
          ) : (
            plans.map((plan, idx) => {
              const headerClass =
                statusToClass[plan.statusKey] || styles.statusSubmitted;
              const isMenuOpen = openMenuId === plan.key;
              const cardTitle =
                plan.topic ||
                plan.book_name ||
                `${t.lessonPlan || "Lesson Plan"} ${idx + 1}`;

              const isApproved = plan.statusKey === "approved";
              const isRejected = plan.statusKey === "rejected";
              const isCompleted = plan.statusKey === "completed";
              const menuLocked = isApproved || isRejected || isCompleted;
              const menuItemDisabledClass =
                menuLocked || planActionSubmitting
                  ? styles.menuItemDisabled
                  : "";

              return (
                <div key={plan.key} className={styles.planCard}>
                  <div className={`${styles.planHeader} ${headerClass}`}>
                    <div className={styles.planHeaderLeft}>
                      <FaBookOpen className={styles.planHeaderIcon} />
                      <span className={styles.planHeaderTitle}>
                        {cardTitle}
                      </span>
                    </div>

                    <div
                      className={styles.menuWrapper}
                      ref={isMenuOpen ? menuWrapperRef : null}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        className={styles.menuButton}
                        onClick={(e) => handleMenuToggle(e, plan.key)}
                        aria-haspopup="true"
                        aria-expanded={isMenuOpen}
                        aria-label={t.moreOptions || "More options"}
                      >
                        <HiDotsVertical />
                      </button>

                      {isMenuOpen && (
                        <div className={styles.menuPopup} role="menu">
                          <button
                            type="button"
                            role="menuitem"
                            className={`${styles.menuItem} ${menuItemDisabledClass}`}
                            onClick={() => {
                              if (menuLocked) {
                                setOpenMenuId(null);
                                return;
                              }
                              handleApprovePlan(plan);
                            }}
                            disabled={menuLocked || planActionSubmitting}
                          >
                            {isApproved
                              ? t.alreadyApproved || "Already Approved"
                              : t.approve || "Approve"}
                          </button>

                          <button
                            type="button"
                            role="menuitem"
                            className={`${styles.menuItem} ${menuItemDisabledClass}`}
                            onClick={() => {
                              if (menuLocked) {
                                setOpenMenuId(null);
                                return;
                              }
                              handleRejectPlan(plan);
                            }}
                            disabled={menuLocked || planActionSubmitting}
                          >
                            {isRejected
                              ? t.alreadyRejected || "Already Rejected"
                              : t.reject || "Reject"}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={styles.metaGrid}>
                    <div className={styles.metaField}>
                      <div className={styles.metaLabel}>{t.book || "Book"}</div>
                      <div className={styles.metaValue}>
                        {plan.book_name || "-"}
                      </div>
                    </div>
                    <div className={styles.metaField}>
                      <div className={styles.metaLabel}>
                        {t.topic || "Topic"}
                      </div>
                      <div className={styles.metaValue}>
                        {plan.topic || "-"}
                      </div>
                    </div>
                    <div className={styles.metaField}>
                      <div className={styles.metaLabel}>
                        {t.subTopic || "Sub Topic"}
                      </div>
                      <div className={styles.metaValue}>
                        {plan.sub_topic || "-"}
                      </div>
                    </div>
                    <div className={styles.metaField}>
                      <div className={styles.metaLabel}>
                        {t.medium || "Medium"}
                      </div>
                      <div className={styles.metaValue}>
                        {plan.medium || "-"}
                      </div>
                    </div>
                  </div>

                  {(plan.start_page != null || plan.end_page != null) && (
                    <div className={styles.metaGrid}>
                      <div className={styles.metaField}>
                        <div className={styles.metaLabel}>
                          {t.startPageLabel || "Start Page"}
                        </div>
                        <div className={styles.metaValue}>
                          {plan.start_page != null ? plan.start_page : "-"}
                        </div>
                      </div>
                      <div className={styles.metaField}>
                        <div className={styles.metaLabel}>
                          {t.endPageLabel || "End Page"}
                        </div>
                        <div className={styles.metaValue}>
                          {plan.end_page != null ? plan.end_page : "-"}
                        </div>
                      </div>
                      <div className={styles.metaField}>
                        <div className={styles.metaLabel}>
                          {t.date || "Date"}
                        </div>
                        <div className={styles.metaValue}>
                          {plan.date
                            ? moment(plan.date).format("DD MMM YYYY")
                            : "-"}
                        </div>
                      </div>
                      <div className={styles.metaField}>
                        <div className={styles.metaLabel}>
                          {t.status || "Status"}
                        </div>
                        <div className={styles.metaValue}>
                          {plan.statusKey
                            ? plan.statusKey.charAt(0).toUpperCase() +
                              plan.statusKey.slice(1)
                            : "-"}
                        </div>
                      </div>
                    </div>
                  )}

                  {plan.staff_comment ? (
                    <div className={styles.feedbackSection}>
                      <div className={styles.sectionLabel}>
                        {t.feedback || "Feedback"}
                      </div>
                      <div className={styles.feedbackText}>
                        {plan.staff_comment}
                      </div>
                    </div>
                  ) : null}

                  <div className={styles.contentSection}>
                    <div className={styles.sectionLabel}>
                      {t.lessonPlan || "Lesson Plan"}
                    </div>
                    <div
                      className={styles.contentBody}
                      dangerouslySetInnerHTML={{ __html: plan.content || "" }}
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* ============ Action Buttons ============ */}
        <div className={styles.actionBar}>
          <Button
            className={`${styles.actionButton} ${styles.cancelButton}`}
            onClick={handleBack}
            disabled={approvingAll}
          >
            {t.cancel || "Cancel"}
          </Button>
          <Button
            className={`${styles.actionButton} ${styles.approveButton}`}
            onClick={handleApproveSelected}
            disabled={approvingAll || !canApproveAll}
          >
            {approvingAll
              ? t.approving || "Approving..."
              : t.approveAll || "Approve All"}
          </Button>
        </div>

        <SendReminderModal
          open={reminderModalOpen}
          onClose={handleCloseReminderModal}
          onConfirm={handleConfirmSendReminder}
          loading={sendingReminder}
          weekRangeLabel={weekRangeLabel}
          periods={periods}
          schedule={schedule}
          translations={translations}
        />

        <RejectionReasonModal
          open={planRejectModal.open}
          onClose={handleCancelRejectPlan}
          onConfirm={handleConfirmRejectPlan}
          loading={planActionSubmitting}
          translations={translations}
        />
      </div>
    </>
  );
};

export default ViewTeacherWiseApproval;